import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Role,
  TableauSiteRole,
  TaskKind,
  User,
} from "../../../../app/backendTypes";
import { useCallback, useContext, useState } from "react";
import { Close } from "@mui/icons-material";
import UserSpan from "../../users/UserSpan";
import Morselbar from "../../Morselbar";
import { HttpClientContext, X_REQUEST_ID } from "../../../../app/client";
import { AxiosError } from "axios";
import { useAppSelector } from "../../../../app/hooks";

export function TableauSiteRoleChangeDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  role: TableauSiteRole | null;
  user: User;
  callback: () => void;
}) {
  const { open, setOpen, role, user, callback } = props;

  const isAdmin = useAppSelector((state) => state.auth.roles).includes(
    Role.Administrator
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [taskNote, setTaskNote] = useState<string>("");
  const [override, setOverride] = useState(false);

  const client = useContext(HttpClientContext);

  const createTableauSiteRoleChangeTask = useCallback(() => {
    if (role === null) return;

    setLoading(true);
    setError(undefined);

    const note = taskNote || undefined;

    const body: TaskKind = {
      kind: "TableauSiteRoleChange",
      inner: {
        target: user.employee.id,
        role,
        note,
      },
    };

    client
      ?.post(`/api/tasks?override_approvals=${override}`, body)
      .then(() => {
        setOpen(false);
        callback();
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [callback, client, override, role, setOpen, taskNote, user.employee.id]);

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              This action will change <UserSpan user={user} />
              's site role in{" "}
              <Typography component="span" color="secondary">
                Tableau
              </Typography>{" "}
              to{" "}
              <Typography component="span" color="primary">
                {role}
              </Typography>
            </Typography>

            <IconButton
              onClick={() => {
                setOpen(false);
              }}
            >
              <Close />
            </IconButton>
          </Box>

          <br />

          <TextField
            onChange={(e) => setTaskNote(e.target.value.trim())}
            placeholder="(Optional) Add a note"
            label="Note"
            size="small"
            fullWidth
          />

          <br />
          <br />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {isAdmin && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={override}
                        onChange={(e) => setOverride(e.target.checked)}
                      />
                    }
                    label="Skip approvals"
                  />
                </>
              )}
            </Box>

            <Button
              onClick={() => createTableauSiteRoleChangeTask()}
              disabled={loading}
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to create task"
        error={error}
      />
    </>
  );
}
